import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { TopBar } from "../components/TopBar";
import { PeriodBadge } from "../components/PeriodBadge";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Building2, Share2, ArrowRight, History, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MainSelectionPage() {
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);
  const [periodKey, setPeriodKey] = useState(0);

  const portals = [
    {
      title: "Brands",
      subtitle: "Track traffic data and balances per brand",
      icon: Building2,
      to: "/brands",
      testId: "portal-brands-button",
    },
    {
      title: "Sources",
      subtitle: "Track traffic with PH / QG balances per source",
      icon: Share2,
      to: "/sources",
      testId: "portal-sources-button",
    },
  ];

  const resetWeek = async () => {
    setResetting(true);
    try {
      await api.post("/period/reset");
      toast.success("Week closed and archived. New accounting period started.");
      setPeriodKey((k) => k + 1);
    } catch (e) {
      toast.error("Failed to reset week");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[]} />
      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Select a portal
        </h1>
        <p className="mt-2 text-muted-foreground">
          Brands and Sources are managed separately with different financial logic.
        </p>
        <div className="mt-4">
          <PeriodBadge key={periodKey} />
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          {portals.map((p) => (
            <Card
              key={p.title}
              data-testid={p.testId}
              role="button"
              tabIndex={0}
              onClick={() => navigate(p.to)}
              onKeyDown={(e) => e.key === "Enter" && navigate(p.to)}
              className="group cursor-pointer border-border shadow-sm transition-colors duration-150 hover:border-primary/50 hover:bg-accent/30"
            >
              <CardContent className="flex items-center gap-5 p-8">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <p.icon className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold tracking-tight">{p.title}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{p.subtitle}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground transition-colors duration-150 group-hover:text-primary" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Admin / settings area */}
        <div className="mt-10 border-t border-border pt-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Administration</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/history")}
              data-testid="weekly-history-button"
            >
              <History className="mr-1.5 h-4 w-4" />
              Weekly History
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="reset-week-button">
                  <RotateCcw className="mr-1.5 h-4 w-4" />
                  Reset Week
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="reset-week-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Week</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to close the current accounting week and start a new one? The current
                    data will be archived and the final balance will become the new Opening Balance.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="reset-week-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={resetWeek} disabled={resetting} data-testid="reset-week-confirm">
                    {resetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Yes, close the week
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            The accounting week runs Monday to Sunday. Every Tuesday the previous week (through Sunday) is
            archived automatically and its final Total becomes the new Opening Balance — Monday activity always
            stays in the new week. Archived weeks remain available in Weekly History.
          </p>
        </div>
      </main>
    </div>
  );
}
